import React from 'react'
import { Box, IconButton, Slider, Stack } from '@mui/material'
import { TimeSeriesRow } from 'common/types'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import dayjs from 'dayjs'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious'
import { isMobileOnly } from 'react-device-detect'
import { useGlobalState } from 'state'


export type TimeSeriesSliderProps = {
  baseDate: dayjs.Dayjs
  currentIndex: number
  timeSeries: TimeSeriesRow[]
  displayLabelsHours?: number[]
  moveend: (index: number) => void
}

export const TimeSeriesSlider = ({
  baseDate,
  currentIndex,
  timeSeries,
  displayLabelsHours = [-3, -2, -1, 0, 1, 6, 12, 24, 36],
  moveend
}: TimeSeriesSliderProps) => {
  const state = useGlobalState()
  const [autoPlay, setAutoPlay] = React.useState(false)

  const getMarks = (): { value: number, label: string}[] => {
    if (timeSeries.length === 0) {
      return []
    }

    const marks = displayLabelsHours.map(h => {
      const targetDate = baseDate.clone().add(h, 'hours').format('YYYY/MM/DD HH:mm')
      const targetIndex = timeSeries.findIndex(row => row.date >= targetDate)
      return {
        value: targetIndex,
        label: h === 0 ? '現在' : h > 0 ? `+${h}h` : `${h}h`
      }
    }).filter(m => m.value >= 0)
    return marks
  }

  const getValueLabelFormat = (index: number) => {
    if (timeSeries.length === 0 || index < 0 || index >= timeSeries.length) {
      return ''
    }
    const row = timeSeries[index]
    const date = dayjs.utc(row.date, 'YYYY/MM/DD HH:mm')
    let tense = '(未来)'
    if (baseDate.isSame(date)) {
      tense = '(現在)'
    } else if (baseDate.isAfter(date)) {
      tense = '(過去)'
    }
    return <Box>{date.local().format('MM/DD HH:mm')} {tense}</Box>
  }

  const theme = createTheme({
    components: {
      MuiSlider: {
        styleOverrides: {
          root: {
            '& .MuiSlider-track': {
              border: 'none',
            },
            '& .MuiSlider-rail': {
              opacity: 0.5,
              // backgroundColor: '#bfbfbf',
            },
            '& .MuiSlider-mark': {
              // backgroundColor: '#bfbfbf',
              height: 8,
              width: 1,
              '&.MuiSlider-markActive': {
                opacity: 1,
                backgroundColor: 'currentColor',
              },
            },
            '& .MuiSlider-markLabel': {
              // color: '#eeeeee'
            },
            '& .MuiSlider-valueLabel': {
              padding: '2px 5px'
            }
          }
        }
      }
    }
  })

  React.useEffect(() => {
    if (autoPlay) {
      const timer = setTimeout(() => {
        if (currentIndex < timeSeries.length-1) {
          moveend(currentIndex + 1)
        } else {
          setAutoPlay(false)
        }
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [autoPlay, currentIndex])

  React.useEffect(() => {
    setAutoPlay(false)
  }, [state.app.baseDate])

  const startAutoPlay = () => {
    if (autoPlay) {
      setAutoPlay(false)
    } else if (timeSeries.length > 0 && currentIndex < timeSeries.length - 1) {
      setAutoPlay(true)
    }
  }

  const nextStep = () => {
    const nextIndex = Math.min(timeSeries.length - 1, currentIndex + 1)
    moveend(nextIndex)
  }

  const prevStep = () => {
    const prevIndex = Math.max(0, currentIndex - 1)
    moveend(prevIndex)
  }

  return (
    <ThemeProvider theme={theme}>
      <Stack spacing={1} direction={'row'} alignItems={'center'}>
        { !isMobileOnly && (
          <>
            <IconButton onClick={prevStep}>
              <SkipPreviousIcon fontSize='large' sx={{color: 'black'}} />
            </IconButton>
            <IconButton onClick={startAutoPlay}>
              { !autoPlay && <PlayArrowIcon fontSize='large' sx={{color: 'black'}} /> }
              { autoPlay && <PauseIcon fontSize='large' sx={{color: 'black'}} /> }
            </IconButton>
            <IconButton onClick={nextStep}>
              <SkipNextIcon fontSize='large' sx={{color: 'black'}} />
            </IconButton>
          </>
        )}
        <Slider
          size='small'
          value={currentIndex}
          valueLabelDisplay='on'
          valueLabelFormat={getValueLabelFormat}
          step={1}
          min={0}
          marks={getMarks()}
          max={timeSeries.length-1}
          onChange={(e, value) => moveend(value as number)}
        />
      </Stack>
    </ThemeProvider>
  )
}
